import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  Camera,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Phase 3 piece targeting "organize screenshots mac" and "auto rename
 * screenshots mac". Pulled directly from demand mining (5 years of screenshot
 * chaos, useless timestamp filenames, "an enormous folder of screenshots I
 * take of stuff to remember"). Walks through native macOS settings first (so
 * the post is useful even without Sortio), then explains why timestamps are
 * not enough, then shows how Sortio routes screenshots into dated folders
 * with clean names, paired with macOS Live Text for finding text in images.
 * NOTE: Sortio does not OCR images; this page must never claim it reads
 * screenshot pixels.
 */

const PUBLISHED = '2026-05-14';
const CANONICAL = 'https://www.getsortio.com/blog/tame-screenshot-chaos-mac';

const FAQ = [
  {
    question: 'Where does macOS save screenshots by default?',
    answer:
      'The default is the Desktop. You can change it by opening the Screenshot app (Command-Shift-5), clicking Options, and picking a new default location from the menu. Common picks: ~/Pictures/Screenshots, ~/Documents/Screenshots, or a cloud-synced folder like iCloud Drive. macOS remembers the change globally so every future screenshot goes to the new location.',
  },
  {
    question: 'How do I auto rename screenshots based on their content on Mac?',
    answer:
      'macOS does not rename screenshots by content, and neither does Sortio: Sortio does not read the pixels inside an image, so true content-based renaming of screenshots is not something it can do today. What works in practice is a two-part fix. Sortio renames screenshots to a clean date-first template and routes them into monthly folders automatically, and macOS Live Text makes the text inside screenshots searchable through Spotlight, so you find the right image by what it says without it being in the filename.',
  },
  {
    question: 'Can I sort screenshots into folders by what is in them?',
    answer:
      'Not by what is inside the image. Sortio routes screenshots using their filename, dates, and metadata, so it can split them by month, by source app when the app puts its name in the filename (CleanShot, Zoom recordings, simulator captures), and by your own naming habits. It cannot look at the pixels and decide "this is a receipt." For finding a screenshot by its visible text, use Spotlight: modern macOS indexes text inside images via Live Text.',
  },
  {
    question: 'Should I keep five years of screenshots or just delete them?',
    answer:
      'Triage is healthier than wholesale delete. Most screenshots are ephemeral (a wifi password, a confirmation code, a short-lived chat message). A smaller subset has real reference value (an error message you fixed once, a receipt, a screenshot of a design that shipped). The sort itself surfaces this: when you can see your screenshots organized by content, the keepers are obvious and the rest is easy to delete in bulk.',
  },
  {
    question: 'How do I change the screenshot filename prefix from "Screen Shot" to something else?',
    answer:
      'There is a Terminal command for this. Run defaults write com.apple.screencapture name "Cap" (replacing Cap with your preferred prefix), then killall SystemUIServer. Future screenshots will use your prefix. To reset, use defaults write com.apple.screencapture name "Screen Shot". This only changes the prefix, not the timestamp, and it does not help with content discovery, but if the "Screen Shot" prefix annoys you it is a one-line fix.',
  },
  {
    question: 'Can I include screenshots in a Sortio watch folder?',
    answer:
      'Yes. On Pro, point a watch folder at ~/Pictures/Screenshots (or wherever you save them), with a prompt that says "rename to YYYY-MM-DD_HHMM_Screenshot.png and route into YYYY-MM subfolders." Every screenshot you take is renamed within seconds of being saved, which means the Screenshots folder stays tidy automatically without needing manual cleanup runs.',
  },
];

const ScreenshotChaosBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Tame screenshot chaos on Mac', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Tame screenshot chaos on Mac',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Tame Screenshot Chaos on Mac: Organize, Rename, and Route Automatically',
    description:
      'Mac screenshots eat the Desktop with useless timestamp filenames. Change the save location and prefix, route screenshots into dated folders with Sortio, and use macOS Live Text to search the text inside them.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: PUBLISHED,
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="Tame Screenshot Chaos on Mac: Organize and Auto-Route Screenshots"
        description="Stop the Mac screenshot pile. Move screenshots off the Desktop, change the default location and prefix, then use Sortio to route them into dated folders and Live Text to search inside them."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="organize screenshots mac, auto rename screenshots mac, screenshot chaos, screenshot folder mac, sort screenshots into folders, mac screenshot location"
        lastModified={PUBLISHED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            Tame Screenshot Chaos on Mac: Organize, Rename, and Route Automatically
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>8 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <p className="text-lg">
            Look at your Desktop. If it is a mosaic of files all named "Screen Shot 2026-04-12 at
            3.47.18 PM.png," you are in good company. The single most quoted complaint in the
            Mac-organization corner of the internet is "I have an enormous folder of screenshots
            I take of stuff to remember" and "5 years of screenshot chaos." The screenshots are
            useful in the moment and then unfindable afterwards, because nothing about the
            timestamp tells you what is in the image.
          </p>

          <p>
            This post is the full fix in three layers. Native macOS settings get the screenshots
            off the Desktop and into a sensible folder. A small Terminal command changes the
            prefix from "Screen Shot" to something you can search by. And a Sortio prompt routes
            every screenshot into a dated folder structure with clean names, while macOS Live
            Text handles finding the text inside the images.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <Camera className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  Move screenshots to ~/Pictures/Screenshots via Command-Shift-5 Options. Set a
                  shorter prefix with a one-line defaults command. Run Sortio over the folder to
                  route everything into monthly subfolders with clean date-first names. On Pro,
                  turn it into a watch folder so every new screenshot is auto-filed as it lands.
                  For finding a screenshot by what it says, Spotlight searches the text inside
                  images via Live Text.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Step 1: Move screenshots off the Desktop
          </h2>

          <p>
            macOS dumps screenshots on the Desktop by default. The Desktop is the worst possible
            place for them: every app you open covers them up, Finder slows down once there are
            a few hundred, and the visual noise undermines the entire point of having a clean
            workspace.
          </p>

          <p>
            Press Command-Shift-5 to open the macOS Screenshot app. Click Options. Under "Save
            to," choose Other Location and pick (or create) ~/Pictures/Screenshots. macOS
            remembers this globally, so every future screenshot, every key combination
            (Command-Shift-3, Command-Shift-4, the touch bar shortcuts), and every third-party
            screenshot tool that respects the macOS default writes there.
          </p>

          <p>
            For the existing pile on the Desktop, drag them into the new folder in one move.
            Finder will not complain and Spotlight will reindex over the next few minutes. This
            single move is the biggest visible win in the entire workflow: the Desktop goes from
            chaos to clean in 30 seconds.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Step 2: Change the filename prefix (optional)
          </h2>

          <p>
            macOS prepends "Screen Shot" to every screenshot filename. The space makes shell
            commands awkward and the long prefix wastes characters that could be a real name.
            Two Terminal commands fix this:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`defaults write com.apple.screencapture name "Cap"
killall SystemUIServer`}
          </pre>

          <p>
            Future screenshots will be named Cap 2026-04-12 at 3.47.18 PM.png. Or replace "Cap"
            with whatever you like (one user we know just uses "ss"). To reset:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`defaults write com.apple.screencapture name "Screen Shot"
killall SystemUIServer`}
          </pre>

          <p>
            This is cosmetic. It does not solve discoverability (the timestamp is still useless)
            but it makes the filenames easier to handle in scripts. Skip this step if you do not
            care.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Step 3: Change the format and drop window shadows (optional)
          </h2>

          <p>
            Two more Terminal one-liners that ship with most Mac-screenshot guides because they
            are worth knowing. Switch the format from PNG (large) to JPG (smaller) if you take a
            lot of full-screen captures:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`defaults write com.apple.screencapture type jpg
killall SystemUIServer`}
          </pre>

          <p>
            And drop the window shadow that Command-Shift-4-Space adds around window captures,
            which is large, often visually noisy, and a hassle when you crop:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`defaults write com.apple.screencapture disable-shadow true
killall SystemUIServer`}
          </pre>

          <p>
            None of this fixes discoverability. That is what step four is about.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Step 4: Why timestamps are not enough
          </h2>

          <p>
            Even after steps 1 through 3, the Screenshots folder is still one flat
            chronological pile. You took a screenshot of an error message six months ago. You
            remember roughly when, you do not remember the exact date. Finder gives you a list
            of timestamps and a few preview thumbnails per screen. The folder is tidier but
            barely more browsable than the Desktop was.
          </p>

          <p>
            The fix has two halves. Structure: monthly subfolders and clean date-first names
            turn a 4,000-file flat pile into something you can actually browse, and that part is
            automatable. Content: for finding a screenshot by what it says, modern macOS already
            indexes the text inside images (Live Text), so a Spotlight search for "connection
            refused" or the vendor on a receipt surfaces the right screenshot even though the
            filename is a timestamp. You do not need a tool that reads your screenshots; you need
            the pile organized so the matches you get back make sense.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Step 5: Use Sortio to file screenshots automatically
          </h2>

          <p>
            Sortio routes and renames screenshots using their filenames, dates, and metadata. To
            be clear about scope: Sortio does not read the pixels inside an image, so it cannot
            decide "this is a receipt" from the picture itself. What it automates is everything
            around that: the dated folder structure, the clean names, and pulling screenshots
            out of mixed folders like Desktop and Downloads.
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Collect every screenshot in this folder (filenames
starting with "Screen Shot", "Cap", "CleanShot",
"Screenshot", or "Simulator Screenshot", extension
.png or .jpg).

Route each to ~/Pictures/Screenshots/{YYYY-MM}/ based
on its capture date, and rename to
{YYYY-MM-DD}_{HHMM}_{source}.png, where source is the
capturing app when the filename names one (CleanShot,
Simulator, Zoom) and "Screenshot" otherwise. Keep any
descriptive words already in the filename. Underscores,
not spaces.`}
          </pre>

          <p>
            Run Preview first. Sortio shows the proposed name and target folder for each
            screenshot, and you can override anything that looks wrong before a single file
            moves. Apply commits the moves. Sortio keeps backup archives of your most recent
            sorts in case you want to undo a sort from inside the app.
          </p>

          <p>
            The result is a Screenshots folder that looks like:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`~/Pictures/Screenshots/
  2026-03/
    2026-03-28_0915_CleanShot.png
    2026-03-30_1842_Screenshot.png
  2026-04/
    2026-04-12_1347_Screenshot.png
    2026-04-13_0931_Simulator.png
    2026-04-15_1710_Zoom.png`}
          </pre>

          <p>
            Spotlight indexes the filenames immediately, and Live Text picks up the words
            inside each image. The screenshot of the psql error you took six months ago is one
            Spotlight search away ("connection refused"), and the monthly folders keep the
            results browsable instead of a 4,000-file wall.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Step 6: Keep it clean with a watch folder
          </h2>

          <p>
            Manual sort runs are fine for clearing the backlog. To keep the folder clean going
            forward, Sortio Pro ($14.99/month or $99/year) watches the Screenshots folder
            continuously. Every new screenshot you take is renamed within a few seconds. You
            never see the timestamp-only filename: it gets replaced before you notice.
          </p>

          <p>
            For the first week, keep the watch in Preview mode so Sortio queues proposed names
            and you confirm them. After a week of clean previews, switch to Apply and let it
            run.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            A note on privacy
          </h2>

          <p>
            Screenshots often contain sensitive content (chat messages, bank screens, work
            material under NDA). Sortio never uploads your images: in the default cloud mode it
            sends filenames and metadata to plan the sort, and with local processing through
            Ollama even those stay on your Mac. The local trade-off is slightly slower
            processing than the managed AI option. For most screenshot workflows, local mode is
            the right pick. See our piece on{' '}
            <Link
              to="/blog/local-ai-vs-cloud-ai-file-organization"
              className="text-sortio-primary hover:underline"
            >
              local AI vs cloud AI for file organization
            </Link>{' '}
            for the full comparison.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/blog/keep-mac-downloads-folder-clean"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Keep your Mac Downloads folder clean
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/automatically-rename-pdfs-by-content"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Automatically rename PDFs by content
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/local-ai-vs-cloud-ai-file-organization"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Local AI vs cloud AI for file organization
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Sort five years of screenshots in one run</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance. Point Sortio at your screenshot folder,
              run Preview, and watch a timestamp-only mess turn into a dated, browsable archive.
              No credit card required.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ScreenshotChaosBlogPage;
